import {
  ResourceSizeSchema,
  type CustomSandboxSize,
  type ResourceSize,
} from '@runloop/reflex-contract';

/**
 * Idempotent, best-effort launch commands that bring up a Docker daemon inside
 * the devbox. Injected by the sandbox provider when {@link SandboxOptions.dockerd}
 * is set, and re-run on every devbox boot and resume (launch commands have no
 * separate resume hook), so the command must no-op when the daemon is already up.
 *
 * Mirrors the Runloop runtime's managed-DinD boot sequence (rage
 * `dockerd.rs`): switch to legacy iptables, then start dockerd and poll it for
 * readiness. We deliberately keep every step best-effort (`|| true`, no
 * `set -e`) so a daemon that refuses to start logs a warning rather than
 * failing the agent launch. The daemon runs as root and creates a
 * `root:docker` socket; rather than depend on the agent session having the
 * `docker` group active (which is what makes plain `docker` fall back to
 * sudo), we relax the socket to world-accessible — acceptable inside a
 * single-tenant dev devbox — so `docker ...` works without sudo.
 */
export const DOCKERD_LAUNCH_COMMANDS: readonly string[] = [
  [
    'if sudo docker info >/dev/null 2>&1; then',
    '  echo "[dockerd] already running";',
    'else',
    '  echo "[dockerd] starting Docker daemon";',
    '  sudo update-alternatives --set iptables /usr/sbin/iptables-legacy >/dev/null 2>&1 || true;',
    '  sudo update-alternatives --set ip6tables /usr/sbin/ip6tables-legacy >/dev/null 2>&1 || true;',
    '  sudo mkdir -p /var/lib/docker /etc/docker || true;',
    "  sudo service docker start >/dev/null 2>&1 || sudo sh -c 'dockerd >/var/log/dockerd.log 2>&1 &' || true;",
    '  for _ in $(seq 1 30); do sudo docker info >/dev/null 2>&1 && break; sleep 1; done;',
    'fi;',
    'if sudo docker info >/dev/null 2>&1; then',
    '  sudo chmod 666 /var/run/docker.sock 2>/dev/null || true;',
    '  echo "[dockerd] ready";',
    'else',
    '  echo "[dockerd] WARNING: Docker daemon did not become ready; continuing without docker";',
    'fi',
  ].join('\n'),
];

/**
 * The {@link SandboxOptions} boolean flags that represent a togglable devbox
 * "capability" (opt-in runtime features surfaced as the launcher's capability
 * chips). Kept as a named union so {@link DEVBOX_CAPABILITIES} entries stay
 * type-checked against the real option fields.
 */
export type DevboxCapabilityField = 'dockerd';

/**
 * The devbox capabilities a user can opt into at launch, in display order. Each
 * entry maps to a boolean {@link SandboxOptions} field, so surfaces that toggle
 * a capability by id (e.g. the launcher's `@sandbox:<capability>` mention
 * command) can resolve the flag from this list instead of hardcoding it. Today
 * Docker-in-Docker is the only capability; new ones join by appending here and
 * by adding their flag to {@link DevboxCapabilityField}.
 */
export const DEVBOX_CAPABILITIES = [
  {
    id: 'dockerd',
    label: 'dockerd',
    description: 'Docker-in-Docker for building containers',
    field: 'dockerd',
  },
] as const satisfies ReadonlyArray<{
  id: string;
  label: string;
  description: string;
  field: DevboxCapabilityField;
}>;

/** Stable id of a devbox capability from {@link DEVBOX_CAPABILITIES}. */
export type DevboxCapabilityId = (typeof DEVBOX_CAPABILITIES)[number]['id'];

/**
 * Selectable devbox sizes shown in the Launch dialog's Advanced section, in
 * display order. The values are the canonical {@link ResourceSize} enum used
 * by every sandbox-provider call; the labels are short human-facing strings.
 *
 * Kept here (not in `web/`) so the Launch dialog and the persona editor render
 * exactly the same set without drifting, and so server-side schema tests can
 * pin the supported values.
 *
 * The per-size resource descriptions mirror Runloop's published predefined
 * sizes (CPU cores · RAM · disk); keep them in sync with
 * https://docs.runloop.ai/docs/devboxes/configuration/sizes. Runloop's
 * `X_SMALL` and `CUSTOM_SIZE` are intentionally not offered here.
 */
export const SANDBOX_RESOURCE_SIZE_OPTIONS: ReadonlyArray<{
  value: ResourceSize;
  label: string;
  /** vCPU cores for this tier; mirrors the leading `N vCPU` in `description`. */
  cpuCores: number;
  description: string;
}> = [
  { value: 'SMALL', label: 'Small', cpuCores: 1, description: '1 vCPU · 2 GB RAM · 4 GB disk' },
  { value: 'MEDIUM', label: 'Medium', cpuCores: 2, description: '2 vCPU · 4 GB RAM · 8 GB disk' },
  { value: 'LARGE', label: 'Large', cpuCores: 2, description: '2 vCPU · 8 GB RAM · 16 GB disk' },
  {
    value: 'X_LARGE',
    label: 'X-Large',
    cpuCores: 4,
    description: '4 vCPU · 16 GB RAM · 16 GB disk',
  },
  {
    value: 'XX_LARGE',
    label: 'XX-Large',
    cpuCores: 8,
    description: '8 vCPU · 32 GB RAM · 16 GB disk',
  },
];

/**
 * vCPU core count for a named devbox size, read from
 * {@link SANDBOX_RESOURCE_SIZE_OPTIONS} (the single source of truth for tier
 * specs). Shared so launch surfaces can badge a tier's scale without
 * re-deriving it from the description string.
 */
export function resourceSizeCpuCores(size: ResourceSize): number {
  return SANDBOX_RESOURCE_SIZE_OPTIONS.find((o) => o.value === size)?.cpuCores ?? 0;
}

/**
 * Default minutes before an idle devbox auto-suspends, used when
 * {@link SandboxOptions.idleTimeMinutes} is unset. Matches the historical
 * hardcoded `idle_time_seconds: 300` the Runloop provider used to ship.
 */
export const DEFAULT_SANDBOX_IDLE_TIME_MINUTES = 5;

/**
 * Default devbox resource size used when {@link SandboxOptions.resourceSize} is
 * unset. Matches the historical hardcoded value the Runloop provider falls back
 * to, so a launch that leaves the size unset gets the same box it always did.
 *
 * The agent runner stamps this onto the persisted `sandboxOptions` so the
 * effective size is recorded on the agent row and surfaces in the UI (the
 * devbox size badge), rather than being silently applied at devbox-create time
 * and lost. Keep this in sync with the provider fallback. Once the Runloop
 * backend honours the blueprint's own `resource_size_request`, this default can
 * be dropped along with the provider fallback.
 */
export const DEFAULT_SANDBOX_RESOURCE_SIZE: ResourceSize = 'X_LARGE';

/** Largest named devbox size that a trial Runloop account can provision. */
export const MAX_TRIAL_RESOURCE_SIZE: ResourceSize = 'MEDIUM';

/** Safe initial size for launch surfaces backed by a trial Runloop account. */
export const DEFAULT_TRIAL_RESOURCE_SIZE: ResourceSize = MAX_TRIAL_RESOURCE_SIZE;

const RESOURCE_SIZE_RANK: Record<ResourceSize, number> = {
  SMALL: 0,
  MEDIUM: 1,
  LARGE: 2,
  X_LARGE: 3,
  XX_LARGE: 4,
};

/** Whether a named devbox size is within the Runloop trial-tier limit. */
export function isResourceSizeAllowedForTrial(size: ResourceSize): boolean {
  return RESOURCE_SIZE_RANK[size] <= RESOURCE_SIZE_RANK[MAX_TRIAL_RESOURCE_SIZE];
}

/**
 * The org-level default devbox size, resolved from the organization's
 * `defaultSandboxSize` setting: either one of the named {@link ResourceSize}
 * tiers or one of the org's own {@link CustomSandboxSize} entries.
 */
export type OrgDefaultSandboxSize =
  | { kind: 'named'; size: ResourceSize }
  | { kind: 'custom'; size: CustomSandboxSize };

/**
 * Resolve an organization's `defaultSandboxSize` setting against its
 * `customSandboxSizes` list. The stored value is either a named
 * {@link ResourceSize} enum value or the name of one of the org's custom
 * sizes; anything else (including a custom size that has since been removed,
 * or no setting at all) resolves to `null`, meaning the platform default
 * ({@link DEFAULT_SANDBOX_RESOURCE_SIZE}) applies. Shared so the server's
 * launch path and the web launcher agree on one resolution rule.
 */
export function resolveOrgDefaultSandboxSize(
  defaultSandboxSize: string | null | undefined,
  customSandboxSizes: readonly CustomSandboxSize[] | null | undefined,
): OrgDefaultSandboxSize | null {
  if (!defaultSandboxSize) return null;
  const named = ResourceSizeSchema.safeParse(defaultSandboxSize);
  if (named.success) return { kind: 'named', size: named.data };
  const custom = (customSandboxSizes ?? []).find((s) => s.name === defaultSandboxSize);
  return custom ? { kind: 'custom', size: custom } : null;
}

/**
 * Preset minutes shown in the "Auto-suspend after" picker. The chip also
 * accepts a free-form custom value (any positive integer), so this list is the
 * curated shortcut, not the validation surface.
 */
export const SANDBOX_IDLE_TIME_OPTIONS: ReadonlyArray<{
  value: number;
  label: string;
}> = [
  { value: 5, label: '5 minutes' },
  { value: 10, label: '10 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 60, label: '60 minutes' },
];
